import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1200);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1200);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  const services = [
    { name: 'Fastpris', path: '/ydelser/fastpris' },
    { name: 'Spot', path: '/ydelser/spot' },
    { name: 'Portefølje', path: '/ydelser/portefoelje' },
    { name: 'Udbud', path: '/ydelser/udbud' },
  ];

  const activeClass = "text-brand-orange font-semibold uppercase tracking-wider text-xs";
  const inactiveClass = "text-brand-dark hover:text-brand-orange transition-colors duration-200 uppercase tracking-wider text-xs font-semibold";

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-brand-light">
      <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex flex-col items-start">
          <img 
            src="https://assets.auxodev.dk/assets/57398cfb-6e5d-4ba8-a3b9-c24b8dc48392/energi-logo1.png" 
            alt="EnergiMælgere.dk Logo" 
            className="h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation (>= 1200px) */}
        {isLargeScreen && (
          <nav className="flex items-center gap-8">
            <NavLink to="/" className={({ isActive }) => isActive ? activeClass : inactiveClass} end>
              Forside
            </NavLink>

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                onMouseEnter={() => setIsDropdownOpen(true)}
                className={`flex items-center gap-1.5 cursor-pointer uppercase tracking-wider text-xs font-semibold ${
                  location.pathname.startsWith('/ydelser') ? 'text-brand-orange' : 'text-brand-dark hover:text-brand-orange'
                } transition-colors duration-200`}
              >
                Ydelser
                <i className={`fa-regular fa-chevron-down text-[10px] transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isDropdownOpen && (
                <div 
                  onMouseLeave={() => setIsDropdownOpen(false)}
                  className="absolute left-0 mt-2 w-64 bg-white border border-brand-light rounded-sm shadow-md py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                >
                  {services.map((service) => (
                    <NavLink
                      key={service.path}
                      to={service.path}
                      className={({ isActive }) => 
                        `block px-5 py-3 text-xs uppercase tracking-wider font-semibold transition-colors duration-150 ${
                          isActive 
                            ? 'bg-brand-light text-brand-orange' 
                            : 'text-brand-dark hover:bg-brand-light hover:text-brand-orange'
                        }`
                      }
                    >
                      {service.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>

            <NavLink to="/om-os" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
              Om os
            </NavLink>
            <NavLink to="/kontakt" className={({ isActive }) => isActive ? activeClass : inactiveClass}>
              Kontakt
            </NavLink>
          </nav>
        )}

        {/* Desktop CTA Button (>= 1200px) */}
        {isLargeScreen && (
          <Link 
            to="/kontakt" 
            className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-3.5 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors duration-200"
          >
            Få et tilbud
          </Link>
        )}

        {/* Burger Button (< 1200px) */}
        {!isLargeScreen && (
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="text-brand-dark hover:text-brand-orange p-2 cursor-pointer transition-colors"
            aria-label="Menu"
          >
            <i className={`fa-regular ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} />
          </button>
        )}
      </div>

      {/* Mobile Menu (< 1200px) */}
      {!isLargeScreen && isOpen && (
        <div className="border-t border-brand-light bg-white py-4 px-6 shadow-xl animate-in slide-in-from-top duration-200">
          <nav className="flex flex-col gap-4">
            <NavLink 
              to="/" 
              className={({ isActive }) => `text-lg py-2 border-b border-brand-light/50 ${isActive ? activeClass : inactiveClass}`}
              end
            >
              Forside
            </NavLink>
            
            {/* Mobile Services List */}
            <div className="flex flex-col gap-2 py-2 border-b border-brand-light/50">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-dark/50 px-2">Ydelser</span>
              {services.map((service) => (
                <NavLink 
                  key={service.path}
                  to={service.path}
                  className={({ isActive }) => `text-xs uppercase tracking-wider font-semibold py-1.5 px-4 rounded-sm ${isActive ? 'bg-brand-light text-brand-orange' : 'text-brand-dark hover:text-brand-orange'}`}
                >
                  {service.name}
                </NavLink>
              ))}
            </div>

            <NavLink 
              to="/om-os" 
              className={({ isActive }) => `text-lg py-2 border-b border-brand-light/50 ${isActive ? activeClass : inactiveClass}`}
            >
              Om os
            </NavLink>
            <NavLink 
              to="/kontakt" 
              className={({ isActive }) => `text-lg py-2 ${isActive ? activeClass : inactiveClass}`}
            >
              Kontakt
            </NavLink>

            <Link 
              to="/kontakt" 
              className="bg-brand-orange hover:bg-brand-orange-hover text-white text-center py-3.5 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors duration-200 mt-4"
            >
              Få et tilbud
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
